import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import {
  Modal,
  Button,
  Icon,
  Label,
  Loader,
  Message,
} from 'semantic-ui-react';

import { authService } from '../../utils/auth-service';

const POLL_INTERVAL_MS = 10000;

const STATUS_COLOR = {
  COMPLETE: 'green',
  PARTIAL: 'yellow',
  FAILED: 'red',
  PROCESSING: 'blue',
};

class AdHocEmailHistoryModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sends: null,
      loading: true,
      refreshing: false,
      error: null,
    };
    this._mounted = false;
    this._pollTimer = null;
  }

  componentDidMount() {
    this._mounted = true;
    this.fetch({ isRefresh: false });
  }

  componentWillUnmount() {
    this._mounted = false;
    this.clearPoll();
  }

  clearPoll = () => {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  fetch = async ({ isRefresh = false } = {}) => {
    if (this._mounted) {
      this.setState(isRefresh ? { refreshing: true } : { loading: true });
    }

    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    const { campaignId } = this.props;

    if (!url) {
      this.setState({
        loading: false,
        refreshing: false,
        error: 'REACT_APP_AD_HOC_EMAIL_URL is not configured.',
      });
      return;
    }

    try {
      const token = authService.getToken();
      const response = await axios.get(`${url}/campaigns/${campaignId}/sends`, {
        timeout: 15000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const sends = _.get(response, 'data.data.sends', []);
      if (!this._mounted) return;
      this.setState({ sends, loading: false, refreshing: false, error: null });

      // Auto-poll while anything is still in flight.
      const stillProcessing = sends.some(s => s.status === 'PROCESSING');
      if (stillProcessing) {
        if (!this._pollTimer) {
          this._pollTimer = setInterval(() => this.fetch({ isRefresh: true }), POLL_INTERVAL_MS);
        }
      } else {
        this.clearPoll();
      }
    } catch (err) {
      if (!this._mounted) return;
      const message = _.get(err, 'response.data.error') || err.message || 'Could not load history.';
      this.setState({ loading: false, refreshing: false, error: message });
    }
  }

  renderRow = send => {
    const { sendId, createdAt, senderName, senderEmail, subject, isTest, counts, status } = send;
    const when = createdAt ? moment.unix(createdAt).format('MMM D, YYYY · h:mm A') : '—';
    const sender = senderName || senderEmail || 'Unknown';

    return (
      <div
        key={sendId}
        style={{
          border: '1px solid #eee',
          borderRadius: 4,
          padding: '12px 16px',
          marginBottom: 8,
          background: '#fff',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
            <div style={{ fontSize: 12, color: '#888' }}>
              {when} · {sender}
              {isTest && (
                <Label size="tiny" basic style={{ marginLeft: 6 }}>TEST</Label>
              )}
            </div>
            <div
              style={{
                fontWeight: 600,
                marginTop: 4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subject || '(no subject)'}
            </div>
            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
              {counts.sent} sent
              {counts.failed > 0 && ` · ${counts.failed} failed`}
              {counts.skipped > 0 && ` · ${counts.skipped} skipped`}
              {counts.pending > 0 && ` · ${counts.pending} pending`}
            </div>
          </div>
          <Label color={STATUS_COLOR[status] || 'grey'} size="small">
            {status === 'PROCESSING' && <Icon loading name="spinner" />}
            {status}
          </Label>
        </div>
      </div>
    );
  }

  render() {
    const { campaignName, onClose } = this.props;
    const { sends, loading, refreshing, error } = this.state;
    const totalCopy = sends ? `${sends.length} send${sends.length === 1 ? '' : 's'}` : ' ';

    return (
      <Modal open size="large" onClose={onClose} closeOnEscape closeOnDimmerClick>
        <Modal.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div>Email sending history</div>
              <div className="small light" style={{ fontWeight: 'normal', fontSize: 13, marginTop: 2 }}>
                Campaign · {campaignName || '—'}
              </div>
            </div>
            <Icon name="close" link onClick={onClose} style={{ marginTop: 4 }} />
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <span className="small light">{totalCopy}</span>
            <Button
              size="tiny"
              basic
              icon
              labelPosition="left"
              loading={refreshing}
              disabled={loading || refreshing}
              onClick={() => this.fetch({ isRefresh: true })}
            >
              <Icon name="refresh" />
              Refresh
            </Button>
          </div>

          {loading && <Loader active inline="centered" content="Loading..." />}

          {!loading && error && (
            <Message negative>
              <Icon name="exclamation circle" />
              {error}
            </Message>
          )}

          {!loading && !error && sends && sends.length === 0 && (
            <Message info>
              <Icon name="info circle" />
              No emails have been sent for this campaign yet.
            </Message>
          )}

          {!loading && !error && sends && sends.length > 0 && (
            <div>{sends.map(this.renderRow)}</div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Close</Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

AdHocEmailHistoryModal.propTypes = {
  campaignId: PropTypes.string.isRequired,
  campaignName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

AdHocEmailHistoryModal.defaultProps = {
  campaignName: '',
};

export default AdHocEmailHistoryModal;



/** WEBPACK FOOTER **
 ** ./src/components/ad-hoc-email-history-modal/index.js
 **/